import {
  Box,
  Grid,
  Typography,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { ThemePalette } from 'mui.theme';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import {
  insightStaticSummary,
  defaultSummary,
  normalizeTagName,
  isInsightMarkedResolved,
} from './utils';
import {
  TitleText,
  InfoLabel,
  InfoValue,
  SeverityChip,
} from './InsightDetails.styles';
import { useState } from 'react';
import MlInsightsHandler from 'handlers/ml-insights.handler';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { SummaryDetails } from './Summary';
import SwitchToggle from 'components/Switch';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const InsightDetails = ({
  enableDarkTheme,
  dataVal,
}: {
  enableDarkTheme: boolean;
  dataVal: Record<string, string>;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [isUpdatingNotificationStatus, setIsUpdatingNotificationStatus] =
    useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const {
    data: { insights },
  } = useAppSelector((state) => state.mlInsightsReducer);

  const pickStr = (v: unknown): string => {
    if (v == null) return '';
    const s = String(v).trim();
    return s;
  };

  const data = insights.find(
    (insight: Record<string, unknown>) =>
      insight.insight === dataVal.insight &&
      insight.timestamp === dataVal.timestamp &&
      insight.device === dataVal.device
  );

  const dataRecord = data as Record<string, unknown> | undefined;

  const tagNamesFromPayload = (() => {
    const fromStore = dataRecord?.tagNames;
    if (
      Array.isArray(fromStore) &&
      fromStore.every((x) => typeof x === 'string')
    ) {
      return fromStore as string[];
    }
    const fromNav = (dataVal as Record<string, unknown>).tagNames;
    if (Array.isArray(fromNav) && fromNav.every((x) => typeof x === 'string')) {
      return fromNav as string[];
    }
    return [] as string[];
  })();

  const rawInsightLabel =
    pickStr(dataVal.issue) ||
    pickStr(dataVal.insight) ||
    pickStr(dataRecord?.issue) ||
    pickStr(dataRecord?.insight) ||
    'Unknown Issue';

  const issue = rawInsightLabel.includes('Clogged Air Filter')
    ? 'Engine Air Filter Clogging'
    : rawInsightLabel;

  const notificationId = data?.notificationId ?? dataVal.notificationId;

  const insightEndRaw =
    (data as Record<string, unknown> | undefined)?.alertEndTs ??
    dataVal.alertEndTs;
  const insightEndTs =
    insightEndRaw !== undefined &&
    insightEndRaw !== null &&
    String(insightEndRaw).trim() !== ''
      ? String(insightEndRaw)
      : '';

  const insightStartRawValue =
    (data as Record<string, unknown> | undefined)?.alertStartTs ??
    (data as Record<string, unknown> | undefined)?.timestamp ??
    dataVal.alertStartTs ??
    dataVal.timestamp ??
    '';

  const insightStartRaw =
    insightStartRawValue !== undefined &&
    insightStartRawValue !== null &&
    String(insightStartRawValue).trim() !== ''
      ? String(insightStartRawValue)
      : '';

  const isTpmsInsight =
    rawInsightLabel === 'Tpms Slow Leak' ||
    rawInsightLabel === 'Tpms Fast Leak';
  // Anomaly-detection rows come from the ML pipeline rather than a rule.
  const isAnomalyInsight =
    pickStr(dataVal.source).toLowerCase() === 'mlnotification';
  const mlInsightsHandler = new MlInsightsHandler();

  const resolutionVal = data?.resolution ?? dataVal.resolution;
  const statusVal = data?.status ?? dataVal.status;
  const isResolved = isInsightMarkedResolved(resolutionVal, statusVal);

  const applyResolvedState = async (wantResolved: boolean) => {
    if (!notificationId) return;
    const isActive = !wantResolved;
    setIsUpdatingNotificationStatus(true);
    try {
      await mlInsightsHandler.updateMlInsightStatus(notificationId, isActive);
      dispatch(
        showNotification({
          message: wantResolved
            ? 'Insight marked as resolved'
            : 'Insight marked as unresolved',
          type: 'success',
        })
      );
    } catch (error) {
      console.error('Failed to update ML insight status:', error);
      dispatch(
        showNotification({
          message: t('failed_to_update_insight_status_please_try_again'),
          type: 'error',
        })
      );
    } finally {
      setIsUpdatingNotificationStatus(false);
    }
  };

  const handleResolutionToggle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!notificationId) return;
    const nextResolved = event.target.checked;
    if (nextResolved) {
      setResolveDialogOpen(true);
      return;
    }
    void applyResolvedState(false);
  };

  const handleConfirmResolve = () => {
    setResolveDialogOpen(false);
    void applyResolvedState(true);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  let display: Record<string, string> =
    insightStaticSummary[issue as keyof typeof insightStaticSummary];

  if (!display) {
    display = defaultSummary;
  }
  return (
    <Grid item lg={9} md={7} sm={12} xs={12}>
      <Box
        sx={{
          padding: '20px 10px 20px 10px',
          backgroundColor: palette.boxBackground,
          borderRadius: '5px',
          border: `1px solid rgba(255, 255, 255, 0.15)`,
          minHeight: isTpmsInsight ? '200px' : '170px',
          maxHeight: isTpmsInsight ? '200px' : '170px',
        }}
      >
        <Grid container spacing={2}>
          <Grid
            item
            container
            xs={12}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  position: 'relative',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('insight_details')}
                </Typography>
                {/*
                  Anomaly insights hide the start/end column, so severity moves
                  up into this row, offset past where the insight name below
                  ends. Absolutely positioned because the stacked label + chip is
                  taller than the title and would otherwise stretch this row and
                  push the insight name down.
                */}
                {isAnomalyInsight && (
                  <Box
                    sx={{
                      position: 'absolute',
                      // Anchor the label's own line to the title's, so the two
                      // read as one row and the chip hangs below it.
                      top: 0,
                      left: '290px',
                      flexShrink: 0,
                    }}
                  >
                    <InfoLabel sx={{ fontSize: '14px', lineHeight: 1.5 }}>
                      {t('severity')}
                    </InfoLabel>
                    <SeverityChip
                      icon={
                        <ErrorIcon
                          sx={{
                            fill: '#FFFFFF',
                          }}
                          fontSize="small"
                        />
                      }
                      label={dataVal?.severity}
                      sx={{
                        flexShrink: 0,
                        backgroundColor:
                          dataVal.severity === 'Very High'
                            ? '#B71C1C' // darker red
                            : dataVal.severity === 'High'
                            ? '#EB5757'
                            : dataVal.severity === 'Medium'
                            ? '#FFA403'
                            : '#ED7A00',
                      }}
                    />
                  </Box>
                )}
                {/*
                  Threshold insights keep start/end, so severity stays in the
                  row below and this slot carries the sensor tags instead —
                  filling the gap to the right of the insight name.
                */}
                {!isAnomalyInsight && tagNamesFromPayload.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: '460px',
                      width: '340px',
                      flexShrink: 0,
                    }}
                  >
                    <InfoLabel>{t('sensor_tags')}</InfoLabel>
                    <Box
                      sx={{
                        maxHeight: '44px',
                        overflowY: 'auto',
                      }}
                    >
                      {tagNamesFromPayload.map((tag) => (
                        <InfoValue key={tag} sx={{ wordBreak: 'break-word' }}>
                          {normalizeTagName(tag)}
                        </InfoValue>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography sx={{ fontSize: '12px' }}>Resolved</Typography>
                  <SwitchToggle
                    checked={isResolved}
                    disabled={!notificationId || isUpdatingNotificationStatus}
                    onChange={handleResolutionToggle}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12} sx={{ pb: 2.5 }}>
            <TitleText
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              sx={{
                cursor: 'help',
              }}
            >
              {rawInsightLabel}
            </TitleText>
            <Popover
              id="summary-popover"
              open={open}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              onClose={handlePopoverClose}
              disableRestoreFocus
              sx={{
                pointerEvents: 'none',
              }}
              PaperProps={{
                onMouseEnter: handlePopoverOpen,
                onMouseLeave: handlePopoverClose,
                sx: {
                  pointerEvents: 'auto',
                  maxWidth: '500px',
                  maxHeight: '600px',
                  overflow: 'auto',
                },
              }}
            >
              <SummaryDetails
                enableDarkTheme={enableDarkTheme}
                data={data as unknown as Record<string, string>}
              />
            </Popover>
          </Grid>

          <Grid item container xs={12} spacing={4}>
            {!isTpmsInsight && (
              <Grid item xs={12} md={isAnomalyInsight ? 3.2 : 3}>
                <InfoLabel sx={{ mb: 1.5 }}>Subsystem</InfoLabel>
                <InfoValue sx={{ height: '60px', overflowY: 'auto' }}>
                  {
                    insightStaticSummary[
                      issue as keyof typeof insightStaticSummary
                    ]?.subsystem
                  }
                </InfoValue>
              </Grid>
            )}

            {/* Threshold insights show sensor tags in the header row instead. */}
            {(isAnomalyInsight || isTpmsInsight) &&
              tagNamesFromPayload.length > 0 && (
                <Grid item xs={12} md={isTpmsInsight ? 2.4 : 3.2}>
                  <InfoLabel>{t('sensor_tags')}</InfoLabel>
                  <InfoValue sx={{ height: '60px', overflowY: 'auto' }}>
                    {tagNamesFromPayload
                      .map((tag) => normalizeTagName(tag))
                      .join(', ')}
                  </InfoValue>
                </Grid>
              )}

            {isTpmsInsight && (
              <Grid item xs={12} md={2.4}>
                <InfoLabel>{t('tire_detail')}</InfoLabel>
                <InfoValue>
                  {data
                    ? ((data as Record<string, unknown>)?.tire as string) ||
                      'N/A'
                    : 'N/A'}
                </InfoValue>

                <InfoLabel>{t('side_of_truck')}</InfoLabel>
                <InfoValue>
                  {data
                    ? ((data as Record<string, unknown>)
                        ?.side_of_truck as string) || 'N/A'
                    : 'N/A'}
                </InfoValue>
              </Grid>
            )}

            {/*
              Insight start / end are hidden for anomaly-detection insights for
              now — the evidence chart's own axis already carries the window.
              Threshold (rule notification) insights still show them, and each
              gets its own column so they read left-to-right rather than stacking
              and making one column twice the height of its neighbours.
            */}
            {!isAnomalyInsight && (
              <Grid item xs={12} md={isTpmsInsight ? 2.4 : 3}>
                <InfoLabel sx={{ mb: 1.5 }}>{t('insight_start')}</InfoLabel>
                <InfoValue>
                  {convertUtcToDisplayTime(
                    insightStartRaw,
                    preferredTimeDisplayMode,
                    timezone,
                    'YYYY-MM-DD HH:mm'
                  )}
                </InfoValue>
              </Grid>
            )}

            {!isAnomalyInsight && (
              <Grid item xs={12} md={isTpmsInsight ? 2.4 : 3}>
                <InfoLabel sx={{ mb: 1.5 }}>{t('insight_end')}</InfoLabel>
                <InfoValue>
                  {insightEndTs
                    ? convertUtcToDisplayTime(
                        insightEndTs,
                        preferredTimeDisplayMode,
                        timezone,
                        'YYYY-MM-DD HH:mm'
                      )
                    : 'N/A'}
                </InfoValue>
              </Grid>
            )}

            {/* Anomaly insights render severity in the header row instead. */}
            {!isAnomalyInsight && (
              <Grid item xs={12} md={isTpmsInsight ? 2.4 : 3}>
                <InfoLabel>{t('severity')}</InfoLabel>
                <Box sx={{ mt: 0.5 }}>
                  <SeverityChip
                    icon={
                      <ErrorIcon
                        sx={{
                          fill: '#FFFFFF',
                        }}
                        fontSize="small"
                      />
                    }
                    label={dataVal?.severity}
                    sx={{
                      backgroundColor:
                        dataVal.severity === 'Very High'
                          ? '#B71C1C' // darker red
                          : dataVal.severity === 'High'
                          ? '#EB5757'
                          : dataVal.severity === 'Medium'
                          ? '#FFA403'
                          : '#ED7A00',
                    }}
                  />
                </Box>
              </Grid>
            )}

            {/* <Grid item xs={12} md={isTpmsInsight ? 2 : 2}>
              <InfoLabel>Status</InfoLabel>
              <Typography
                sx={{
                  fontSize: '12px',
                  backgroundColor:
                    data?.resolution === 'Active' ? '#EB5757' : '#009161',
                  borderRadius: '4px',
                  padding: '5px',
                  paddingBottom: '5px',
                }}
              >
                {data?.resolution === 'Inactive' ? 'Solved' : data?.resolution}
              </Typography>
            </Grid> */}
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={resolveDialogOpen}
        onClose={() => {
          setResolveDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: palette.boxBackground,
            color: palette.textPrimary,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '16px' }}>
          {t('mark_as_resolved')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('do_you_want_to_mark_this_as_resolved')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setResolveDialogOpen(false);
            }}
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmResolve}
            disabled={isUpdatingNotificationStatus}
          >
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
